// ─── Feuille de création / édition d'une NOTE de journal ──────────
// Convention app-wide (08/08) : icône d'action **＋** tant que la note
// n'existe pas, **💾** seulement en édition.
// Le sélecteur de date est une `Feuille` : il ne peut pas être rendu ICI
// (deux modales imbriquées figent l'app sur natif). On le DEMANDE à l'écran
// via `onDemanderDate`, qui le rend en sœur — même montage que FeuilleContact.
import React, { useEffect, useMemo, useState } from 'react';
import { Text, TextInput, StyleSheet } from 'react-native';
import { Feuille, ChoixPuces, ChampBouton, stylesForm, useUI, useT, frJMA } from '@mop/core';
import {
  optionsNature, optionsValeur, accepteValeurLibre, VALEUR_LIBRE, natureDe,
} from './data/journal';

const auj = () => new Date().toISOString().slice(0, 10);

export default function FeuilleNote({ visible, initial, onDemanderDate, onDemanderHeure, onFermer, onEnregistrer, erreur }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const [nature, setNature] = useState('note');
  const [valeur, setValeur] = useState('');       // id du vocabulaire OU VALEUR_LIBRE
  const [libre, setLibre]   = useState('');
  const [date, setDate]     = useState(auj());
  const [texte, setTexte]   = useState('');
  const [heure, setHeure]   = useState('');

  // Reprise de la note à chaque ouverture. Une note dont `valeurLibre` est
  // renseignée rouvre sur la puce « Autre… », avec son texte.
  useEffect(() => {
    if (!visible) return;
    const i = initial || {};
    const nat = natureDe(i);
    setNature(nat);
    setLibre(i.valeurLibre || '');
    setValeur(i.valeurLibre ? VALEUR_LIBRE : (i.valeurId || ''));
    setDate(i.date || auj());
    setTexte(i.texte || '');
    setHeure(i.heure || '');
  }, [visible, initial]);

  // Changer de nature change le VOCABULAIRE : la valeur d'avant n'y existe
  // plus, on la remet à zéro plutôt que de laisser une sélection fantôme.
  const changerNature = (n) => {
    setNature(n);
    setValeur('');
    setLibre('');
  };

  // Une puce déjà active se désélectionne : une note peut n'avoir aucune
  // valeur, c'est un cas normal (cf. tests).
  const choisirValeur = (id) => {
    const off = id === valeur;
    setValeur(off ? '' : id);
    if (off || id !== VALEUR_LIBRE) setLibre('');
  };

  const libreOuvert = accepteValeurLibre(nature) && valeur === VALEUR_LIBRE;
  const note = {
    nature,
    valeurId: valeur === VALEUR_LIBRE ? '' : valeur,
    valeurLibre: libreOuvert ? libre : '',
    date,
    texte,
    heure,
  };

  // Rien à enregistrer sans texte : c'est le contenu de la note. Et en
  // édition, la disquette reste grise tant que rien n'a bougé.
  const i = initial || {};
  const inchange = !!i.id
    && note.nature === natureDe(i)
    && note.valeurId === (i.valeurId || '')
    && note.valeurLibre === (i.valeurLibre || '')
    && note.date === (i.date || '')
    && note.texte.trim() === (i.texte || '').trim()
    && (note.heure || '') === (i.heure || '');
  const pret = !!texte.trim() && !inchange;

  return (
    <Feuille
      visible={visible}
      titre={i.id ? t('journal.modifierNote') : t('journal.nouvelleNote')}
      emoji="📔"
      hauteur={0.82}
      onFermer={onFermer}
      action={{
        icone: i.id ? '💾' : '＋',
        label: t('commun.enregistrer'),
        disabled: !pret,
        onPress: () => onEnregistrer?.(note),
      }}
    >
      <Text style={s.lbl}>{t('journal.nature')}</Text>
      <ChoixPuces options={optionsNature(t)} value={nature} onChange={changerNature} pleine />

      <Text style={s.lbl}>{t(`journal.valeur_${nature}`)}</Text>
      <ChoixPuces options={optionsValeur(nature, t)} value={valeur} onChange={choisirValeur} />

      {/* Saisie libre : ouverte par la puce « Autre… », et seulement sur les
          natures qui l'autorisent. Elle vaut pour CETTE note — aucune liste
          n'est enrichie (décision de Cédric, 09/08). */}
      {libreOuvert && (
        <>
          <TextInput
            style={[s.input, { marginTop: 8 }]}
            value={libre}
            onChangeText={setLibre}
            placeholder={t('journal.phValeurLibre')}
            placeholderTextColor={UI.faint}
          />
          <Text style={s.aide}>{t('journal.aideValeurLibre')}</Text>
        </>
      )}

      <Text style={s.lbl}>{t('journal.date')}</Text>
      <ChampBouton
        icone="📅"
        valeur={frJMA(date)}
        onPress={() => onDemanderDate?.({ valeur: date, onChoisir: (dk) => setDate(dk || auj()) })}
      />

      <Text style={s.lbl}>{t('journal.heure')}</Text>
      <ChampBouton
        icone="🕑"
        valeur={heure || '—'}
        onPress={() => onDemanderHeure?.({ valeur: heure, onChoisir: (hm) => setHeure(hm || '') })}
      />
      <Text style={s.aide}>{t('journal.heureAide')}</Text>

      <Text style={s.lbl}>{t('journal.texte')}</Text>
      <TextInput
        style={[s.input, s.zone]}
        value={texte}
        onChangeText={setTexte}
        placeholder={t('journal.placeholderNote')}
        placeholderTextColor={UI.faint}
        multiline
      />

      {!!erreur && <Text style={s.err}>{erreur}</Text>}
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  zone: { minHeight: 90, textAlignVertical: 'top' },
  aide: { fontSize: 11.5, color: UI.faint, marginTop: 5 },
  err:  { fontSize: 12, color: UI.danger || '#e5484d', fontWeight: '700', marginTop: 10 },
});
